import React from 'react';
import { Button, Grid } from '@mui/material';

import { type RelationType } from '../sections/DetailsSectionFlow';
import useSettings from 'app/main/components/hooks/useSettings';
import Form from 'app/main/components/Form';
import ResponsiveDialog from 'app/main/components/dialogs/ResponsiveDialog';
import ModuleSearchField from 'app/main/components/fields/ModuleSearchField';
import SelectField from 'app/main/components/fields/SelectField';

interface FlowAddParentDialogProps {
	open: boolean;
	closeDialog: () => void;
	module: string;
	itemUid: string;
	newRelations: RelationType[];
	sendChange: (name: string, newValue: string | null) => void;
}

interface NewRelationType {
	module: string;
	name: string;
	uid: string | null;
}

export default function FlowAddParentDialog(props: FlowAddParentDialogProps) {
	const { settings, t } = useSettings(props.module);

	const [newRelation, setNewRelation] = React.useState<NewRelationType>({
		module: props.newRelations[0]?.module,
		name: props.newRelations[0]?.name,
		uid: null
	});

	const options = props.newRelations.map(option => ({
		value: option.module,
		title: t(`MODULE_${option.module}_NAME`)
	}));

	function addParent() {
		props.sendChange(newRelation.name, newRelation.uid);
		props.closeDialog();
	}

	return (
		<ResponsiveDialog
			open={props.open}
			onClose={props.closeDialog}
			maxWidth="sm"
			title={t('DETAILS_FLOW_DIALOG_ADD_PARENT_TITLE')}
			content={
				<Form id="flowAddParentDialog" onSubmit={addParent}>
					{props.newRelations.length > 0 && (
						<Grid container direction="column" spacing={1}>
							<Grid>
								<SelectField
									name="relationModule"
									label={t('DETAILS_FLOW_FIELD_PARENT_TYPE')}
									options={options}
									value={newRelation.module}
									isEdit
									sendChange={(_name, value) => {
										const newValue = props.newRelations.find(option => option.module === value);
										if (newValue)
											setNewRelation({ module: newValue.module, name: newValue.name, uid: null });
									}}
									disableEmptyOption
								/>
							</Grid>
							<Grid>
								<ModuleSearchField
									name="relationValue"
									label={t(`MODULE_${newRelation.module}_NAME_SINGULAR`)}
									module={newRelation.module}
									uid={newRelation.uid}
									filters={[
										{
											filt_field: `${settings.modu_short}_uid`,
											filt_clause: 'not_equal',
											filt_value: props.itemUid
										}
									]}
									isEdit={!!newRelation.name}
									sendChange={(_name, value) => setNewRelation(state => ({ ...state, uid: value }))}
									required
									validationError={t('ERROR_TEXT_REQUIRED')}
								/>
							</Grid>
						</Grid>
					)}
				</Form>
			}
			actions={
				<>
					<Button onClick={props.closeDialog}>{t('DIALOG_ACTION_CANCEL')}</Button>
					<Button type="submit" form="flowAddParentDialog">
						{t('DIALOG_ACTION_CONFIRM')}
					</Button>
				</>
			}
		/>
	);
}
